import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Tile, Eyebrow, BTN_PRIMARY } from '@/components/bento';
import {
  ArrowRight, Code2, Smartphone, ShoppingBag,
  BarChart3, Palette, Globe, CheckCircle2, Loader2, Send
} from 'lucide-react';

// ─── Custom Builds ────────────────────────────────────────────────────────────
// The body of the /CustomBuilds page: the offer on the left, the project brief
// form on the right, sent through Web3Forms. It used to close the homepage as
// "Have a big idea?" (components/home/HeroSection.jsx) and has its own page now,
// linked from the navbar's Resources menu and the footer.

// ─── Data ─────────────────────────────────────────────────────────────────────

const PROJECT_TYPES = [
  { label: 'Website',      icon: Globe        },
  { label: 'Mobile App',   icon: Smartphone   },
  { label: 'Web App',      icon: Code2        },
  { label: 'E-Commerce',   icon: ShoppingBag  },
  { label: 'Dashboard',    icon: BarChart3     },
  { label: 'Branding',     icon: Palette      },
];

const BUDGETS = ['< $500', '$500–$2k', '$2k–$10k', '$10k+', 'Let\'s talk'];

// ─── Component ────────────────────────────────────────────────────────────────

export default function CustomBuildsSection() {
  const [form, setForm] = useState({
    name: '', email: '', projectType: '', budget: '', description: ''
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) return;

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: '00ed92cb-5032-4578-b9be-306700b2271e',
          subject: `New BuildersLab Project Request — ${form.projectType || 'General'} from ${form.name}`,
          from_name: form.name,
          email: form.email,
          projectType: form.projectType || 'Not specified',
          budget: form.budget || 'Not specified',
          description: form.description,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setStatus('success');
        setForm({ name: '', email: '', projectType: '', budget: '', description: '' });
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    }
  };

  const inputCls = `w-full h-12 px-4 rounded-xl border border-zinc-800 bg-zinc-950 text-white text-sm
    outline-none placeholder-zinc-500 transition-all
    focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20
    hover:border-zinc-700`;

  return (
    <section id="custom-builds" className="bg-zinc-950 pt-4 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 items-stretch">

          {/* ── Left tile: the offer and the promises ── */}
          <Tile className="lg:col-span-5 p-6 sm:p-8 flex flex-col">
            <Eyebrow>Custom builds</Eyebrow>

            <h1 className="mt-5 text-4xl md:text-5xl font-extrabold tracking-[-0.045em] text-white leading-[1.02]">
              Have a big idea?<br />
              <span className="text-orange-500">Let's build it.</span>
            </h1>

            <p className="mt-4 text-zinc-400 text-[15px] leading-relaxed">
              Whether it's a website, mobile app, SaaS platform, or full digital brand — our team engineers it to perfection. Tell us what you need.
            </p>

            {/* Promise cards */}
            <div className="mt-8 space-y-5">
              {[
                {
                  title: 'Custom, not templated',
                  body: 'Every project is architectural from scratch. No Wix, no drag-and-drop shortcuts.',
                },
                {
                  title: 'Engineered to convert',
                  body: 'Fast, SEO-optimised, and built around your business goals — not just for aesthetics.',
                },
                {
                  title: 'Yours from day one',
                  body: 'Full handover of code, assets and documentation. You own everything we build.',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-3.5">
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-orange-500/15 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-3.5 h-3.5 text-orange-400" />
                  </span>
                  <div>
                    <p className="text-white font-bold text-sm mb-0.5">{item.title}</p>
                    <p className="text-zinc-400 text-sm leading-relaxed">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Social proof blurb. mt-auto pins it to the bottom when the form
                beside it is the taller tile; pt-8 is the gap it keeps when this
                tile is the taller one and there is no slack to push with. */}
            <div className="mt-auto pt-8">
              <div className="pt-6 border-t border-zinc-800">
                <p className="text-zinc-500 text-xs uppercase tracking-widest font-semibold mb-3">Trusted for projects in</p>
                <div className="flex flex-wrap gap-2">
                  {['Real Estate', 'Fintech', 'E-Commerce', 'SaaS', 'Healthcare', 'Media'].map(tag => (
                    <span key={tag} className="px-3 py-1.5 rounded-full bg-zinc-800 text-zinc-400 text-xs font-semibold">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Tile>

          {/* ── Right tile: Contact form ── */}
          <div className="lg:col-span-7">
            <div className="h-full bg-zinc-900 border border-zinc-800 rounded-3xl p-6 sm:p-8">

              <AnimatePresence mode="wait">
                {status === 'success' ? (
                  <div
                    key="success"
                   
                   
                    className="flex flex-col items-center justify-center py-16 text-center gap-5"
                  >
                    <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-xl mb-2">Request Received 🎉</h3>
                      <p className="text-zinc-400 text-sm max-w-xs">
                        We'll review your project and get back to you within 24 hours. Exciting things ahead!
                      </p>
                    </div>
                    <button
                      onClick={() => setStatus('idle')}
                      className="text-orange-400 text-sm font-medium hover:text-orange-300 transition-colors"
                    >
                      Submit another request →
                    </button>
                  </div>
                ) : (
                  <form
                    key="form"
                   
                   
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    {/* Name + Email */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                          Your Name *
                        </label>
                        <input
                          required
                          type="text"
                          value={form.name}
                          onChange={e => set('name', e.target.value)}
                          placeholder="John Doe"
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                          Email Address *
                        </label>
                        <input
                          required
                          type="email"
                          value={form.email}
                          onChange={e => set('email', e.target.value)}
                          placeholder="john@email.com"
                          className={inputCls}
                        />
                      </div>
                    </div>

                    {/* Project type pills */}
                    <div>
                      <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">
                        Project Type
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {PROJECT_TYPES.map(({ label, icon: Icon }) => (
                          <button
                            key={label}
                            type="button"
                            onClick={() => set('projectType', form.projectType === label ? '' : label)}
                            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold border transition-all ${
                              form.projectType === label
                                ? 'bg-orange-500/20 border-orange-500/40 text-orange-300'
                                : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-white'
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    

                    {/* Description */}
                    <div>
                      <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                        Tell us about the project *
                      </label>
                      <textarea
                        required
                        value={form.description}
                        onChange={e => set('description', e.target.value)}
                        placeholder="Describe what you want to build, your target audience, any reference sites, and your timeline…"
                        rows={5}
                        className={`${inputCls} h-auto py-3 resize-none`}
                      />
                    </div>

                    {/* Error */}
                    {status === 'error' && (
                      <p
                       
                       
                        className="text-red-400 text-sm"
                      >
                        {errorMsg}
                      </p>
                    )}

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className={`w-full h-13 flex items-center justify-center gap-2 px-6 py-4 rounded-full
                        disabled:opacity-60 disabled:cursor-not-allowed font-bold text-sm transition-colors ${BTN_PRIMARY}`}
                    >
                      {status === 'loading' ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                      ) : (
                        <><Send className="w-4 h-4" /> Send Project Brief <ArrowRight className="w-4 h-4" /></>
                      )}
                    </button>

                    <p className="text-center text-zinc-500 text-xs">
                      We reply within 24 hours.
                    </p>
                  </form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}